import { VisitorInfo } from "@/shared/types/chat-widget-visitor";
import { UserInfoFormValues } from "@/components/UserInfoForm";
import { resolveAgentWebhookUrl } from "@/lib/webhook-endpoint";
import { isSilentMessage } from "@/utils/message-parser";
import { fetchMessages, ApiMessageRow } from "@/lib/widget-session";
import { ASSET_BASE } from "./asset-base";

// Vercel deployment that hosts the widget's own API routes (config, 2FA).
const WIDGET_API_BASE = ASSET_BASE;

// If neither a bot nor a human reply arrives within this window, stop showing
// the typing indicator — the send genuinely failed or the agent isn't going
// to answer. Without this a failed webhook left the dots forever.
const REPLY_TIMEOUT_MS = 90_000;

// Conversation reads go through the token-authenticated api/messages and new
// replies arrive via POLLING (the anon realtime subscription is gone — that's
// what lets helpdesk_messages get RLS'd with no anon consumer left). 2.5s
// while the chat is open and the tab visible matches perceived-instant.
const POLL_MS = 2_500;

export interface Message {
  text: string;
  isUser: boolean;
  isLoading?: boolean;
  createdAt?: string;
  // Web values plus, on merged-channel (WhatsApp deep-link) sessions, the
  // whatsapp trio: "user-whatsapp" | "bot-whatsapp" | "human-whatsapp".
  senderType?: string;
  mediaUrl?: string;
  mediaType?: string;
  shopifyProducts?: string;
  // Quoted replies (10 Oct 2026). `id` is the stored row id (absent on a
  // visitor message rendered optimistically, which carries `clientMessageId`,
  // the `web_…` id the webhook stores on the row instead). `replyKey` is what
  // a reply to THIS message sends as replyContextId; `replyTo` is the quoted
  // parent as rendered inside the bubble.
  id?: string;
  clientMessageId?: string;
  replyTo?: QuotedMessage | null;
}

export interface QuotedMessage {
  id?: string;
  text: string;
  senderType?: string;
  mediaType?: string | null;
}

/** The key a reply to `m` sends: the row id when stored, else the client id. */
export function replyKeyFor(m: Message): string | null {
  return m.id || m.clientMessageId || null;
}

// Rows the poll appends as agent/staff replies. WhatsApp values only ever
// arrive on merged-channel sessions (api/messages gates on the token), so
// listing them here is inert for ordinary web widgets.
const REPLY_SENDER_TYPES = [
  "bot-web",
  "human-web",
  "bot-whatsapp",
  "human-whatsapp",
];

export interface ChatEngineConfig {
  organizationId: string;
  widgetId: string;
  visitorInfo: VisitorInfo | null;
  contactId: string | null;
  userInfo: UserInfoFormValues | null;
  onContactIdChange: (contactId: string) => void;
  onMessagesChange: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  onLoadingChange: (isLoading: boolean) => void;
  twoFa?: boolean;
  // A thread that is already on screen (the visitor came back to the chat
  // from the home screen). The engine remembers what it holds instead of
  // reloading it, so the first poll fetches only what arrived while away.
  knownMessages?: Message[];
}

export type TwoFaChannel = 'whatsapp' | 'email';

/** Where the code actually went — the API picks the channel and tells us. */
export interface TwoFaDelivery {
  ok: boolean;
  channel?: TwoFaChannel;
  sentTo?: string;
}

export interface SubmitUserInfoResult {
  twoFaPending: boolean;
  contactId: string;
  delivery?: TwoFaDelivery;
}

export class ChatEngine {
  private config: ChatEngineConfig;
  private replyTimeout: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pollInFlight = false;
  private lastSeenAt: string | null = null;
  private seenIds = new Set<string>();
  private onVisibility = () => {
    if (!document.hidden) void this.pollOnce();
  };

  constructor(config: ChatEngineConfig) {
    this.config = config;
    if (config.knownMessages?.length) this.seedFromMessages(config.knownMessages);
    this.ensurePolling();
  }

  // Remember the stored rows of a thread already rendered. Optimistic sends
  // carry no row id yet and are skipped; if nothing stored is known the
  // baseline stays empty and the caller loads history as for a fresh mount.
  private seedFromMessages(messages: Message[]): void {
    for (const m of messages) {
      if (!m.id) continue;
      this.seenIds.add(m.id);
      if (m.createdAt && (!this.lastSeenAt || m.createdAt > this.lastSeenAt)) {
        this.lastSeenAt = m.createdAt;
      }
    }
  }

  /** True once a stored row is known, i.e. a reload would only repeat what is on screen. */
  hasHistoryBaseline(): boolean {
    return this.lastSeenAt !== null;
  }

  // Get the API URL with organization_id (vert — see resolveAgentWebhookUrl).
  private getApiUrl(): string {
    return resolveAgentWebhookUrl(this.config.organizationId);
  }

  private formatMessage(msg: any): Message {
    const quoted = msg.reply_to;
    return {
      id: typeof msg.id === "string" ? msg.id : undefined,
      replyTo: quoted
        ? {
            id: quoted.id,
            text: typeof quoted.message === "string" ? quoted.message : "",
            senderType: quoted.sender_type,
            mediaType: quoted.media_type ?? null,
          }
        : null,
      text: msg.message,
      // user-web AND (merged sessions) user-whatsapp render as the visitor.
      isUser:
        typeof msg.sender_type === "string" &&
        msg.sender_type.startsWith("user"),
      createdAt: msg.created_at,
      senderType: msg.sender_type,
      isLoading: false,
      mediaUrl: msg.media_url,
      mediaType: msg.media_type,
      // History rows carry the aliased shopify_products column; realtime
      // payloads carry the full more_data jsonb.
      shopifyProducts:
        msg.shopify_products ?? msg.more_data?.shopify_products ?? undefined,
    };
  }

  // Load all web messages for a contact (defaults to the configured contact),
  // through the token-authenticated API. Deliberately NO row limit — the
  // widget shows the full thread.
  async loadMessages(contactId: string | null = this.config.contactId): Promise<void> {
    if (!contactId) {
      console.log("[ChatEngine] No contactId, cannot load messages");
      return;
    }

    try {
      this.config.onLoadingChange(true);

      const rows = await fetchMessages(this.config.organizationId, contactId, {
        order: "asc",
      });
      if (rows === null) {
        console.error("[ChatEngine] Message history fetch failed");
        return;
      }

      console.log("[ChatEngine] Loaded messages via api/messages:", rows.length);
      this.trackRows(rows);

      const formattedMessages = rows
        .filter((msg) => !isSilentMessage(msg.message))
        .map((msg) => this.formatMessage(msg));

      this.config.onMessagesChange(formattedMessages);
    } catch (error) {
      console.error("[ChatEngine] Error loading messages:", error);
    } finally {
      this.config.onLoadingChange(false);
    }
  }

  private trackRows(rows: ApiMessageRow[]): void {
    for (const r of rows) {
      if (r.id) this.seenIds.add(r.id);
      if (!this.lastSeenAt || r.created_at > this.lastSeenAt) {
        this.lastSeenAt = r.created_at;
      }
    }
  }

  // Clear chat (used when starting fresh conversation)
  clearChat(): void {
    console.log("[ChatEngine] Clearing chat");
    this.config.onMessagesChange([]);
  }

  // Poll for new replies (replaces the old anon realtime subscription). Runs
  // while a contact exists; skips ticks when the tab is hidden and catches up
  // immediately on visibility.
  private ensurePolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    document.removeEventListener("visibilitychange", this.onVisibility);

    if (!this.config.contactId) {
      console.log("[ChatEngine] No contactId, polling not started");
      return;
    }

    document.addEventListener("visibilitychange", this.onVisibility);
    this.pollTimer = setInterval(() => {
      if (!document.hidden) void this.pollOnce();
    }, POLL_MS);
    void this.pollOnce();
  }

  private async pollOnce(): Promise<void> {
    const contactId = this.config.contactId;
    if (!contactId || this.pollInFlight) return;
    this.pollInFlight = true;
    try {
      // No baseline yet (history not loaded): only ESTABLISH one from the
      // newest row — rendering must wait for loadMessages, or the thread
      // would appear as replies with no context.
      if (!this.lastSeenAt) {
        const latest = await fetchMessages(this.config.organizationId, contactId, {
          order: "desc",
          limit: 1,
        });
        if (latest?.length && contactId === this.config.contactId) {
          this.trackRows(latest);
        }
        return;
      }

      const rows = await fetchMessages(this.config.organizationId, contactId, {
        order: "asc",
        after: this.lastSeenAt,
      });
      if (rows === null || contactId !== this.config.contactId) return;

      for (const row of rows) {
        const unseen = !row.id || !this.seenIds.has(row.id);
        this.trackRows([row]);
        if (!unseen) continue;

        // Bot/human replies append (web always; whatsapp only ever arrives on
        // merged-channel deep-link sessions). The visitor's own WEB sends are
        // skipped — already rendered optimistically by sendMessage/sendMedia —
        // but their WHATSAPP messages (typed on the phone while this thread is
        // open) are real new content and append as user bubbles.
        if (REPLY_SENDER_TYPES.includes(row.sender_type)) {
          // A reply arrived — stop the typing indicator and its failsafe timer.
          this.clearReplyTimeout();
          this.config.onLoadingChange(false);

          // Silent replies are internal-only — clear the loading state but do
          // NOT render them to the customer (e.g. "carousel sent"/"image sent").
          if (isSilentMessage(row.message)) {
            console.log("[ChatEngine] Silent message — not displaying");
            continue;
          }
          this.config.onMessagesChange((prev: Message[]) => [
            ...prev,
            this.formatMessage(row),
          ]);
        } else if (row.sender_type === "user-whatsapp") {
          if (isSilentMessage(row.message)) continue;
          this.config.onMessagesChange((prev: Message[]) => [
            ...prev,
            this.formatMessage(row),
          ]);
        }
      }
    } finally {
      this.pollInFlight = false;
    }
  }

  /** A quick off-cycle poll shortly after a send, so fast replies feel instant. */
  private pollSoon(): void {
    setTimeout(() => void this.pollOnce(), 900);
  }

  private armReplyTimeout(): void {
    this.clearReplyTimeout();
    this.replyTimeout = setTimeout(() => {
      console.warn("[ChatEngine] No reply within timeout — clearing loading state");
      this.config.onLoadingChange(false);
      this.replyTimeout = null;
    }, REPLY_TIMEOUT_MS);
  }

  private clearReplyTimeout(): void {
    if (this.replyTimeout) {
      clearTimeout(this.replyTimeout);
      this.replyTimeout = null;
    }
  }

  // Single place that builds + posts the agent-webhook event. The response may
  // fail on CORS even when the webhook processed, so callers treat the POST as
  // fire-and-forget and rely on realtime for the reply.
  private async postWebhookEvent(
    event: {
      type: string;
      text: string;
      mediaUrl?: string;
      // The message this one quotes (replyKeyFor): the agent runtime stores it
      // on the row and tells the agent what was quoted.
      replyContextId?: string;
    },
    messageId = ChatEngine.mintMessageId(),
  ): Promise<Response> {
    return fetch(this.getApiUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        conversationId: "web",
        waId: this.config.contactId,
        whatsappMessageId: messageId,
        eventType: "message",
        senderName: this.config.userInfo?.fullName || "Web User",
        owner: false,
        // Lets the agent runtime resolve a per-widget pinned agent
        // (web_chat_widgets.agent_id); ignored when no agent is pinned.
        widgetId: this.config.widgetId,
        ...event,
      }),
    });
  }

  /** The id the webhook stores as the row's whatsapp_message_id; minted up
   *  front so the optimistic bubble can be quoted before the row exists. */
  static mintMessageId(): string {
    return `web_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }

  // Send a text message, optionally quoting an earlier one.
  async sendMessage(messageText: string, replyTo?: Message | null): Promise<void> {
    if (!messageText.trim()) return;

    try {
      const messageId = ChatEngine.mintMessageId();
      const replyContextId = replyTo ? replyKeyFor(replyTo) : null;
      // Add user message immediately
      const userMessage: Message = {
        text: messageText,
        isUser: true,
        senderType: "user-web",
        createdAt: new Date().toISOString(),
        clientMessageId: messageId,
        replyTo: replyTo && replyContextId
          ? { id: replyTo.id, text: replyTo.text, senderType: replyTo.senderType, mediaType: replyTo.mediaType ?? null }
          : null,
      };
      this.config.onMessagesChange((prev: Message[]) => [...prev, userMessage]);
      this.config.onLoadingChange(true);
      this.armReplyTimeout();

      // Check if user info is required
      if (!this.config.contactId && !this.config.userInfo) {
        this.clearReplyTimeout();
        this.config.onLoadingChange(false);
        throw new Error("User info required");
      }

      const response = await this.postWebhookEvent(
        { type: "text", text: messageText, ...(replyContextId ? { replyContextId } : {}) },
        messageId,
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData.contactId && responseData.contactId !== this.config.contactId) {
        // Brand-new contact created by the webhook mid-send — adopt it and
        // start polling for its replies immediately (don't wait for the
        // React state round-trip). A fresh contact's entire history IS this
        // conversation, so poll from zero: bot replies render, the visitor's
        // own rows are skipped (already shown optimistically).
        this.config.contactId = responseData.contactId;
        this.config.onContactIdChange(responseData.contactId);
        this.lastSeenAt = "1970-01-01T00:00:00.000Z";
        this.seenIds.clear();
        this.ensurePolling();
      }

      this.pollSoon();
      // Webhook processed successfully; the bot reply arrives via polling and
      // clears loading (or the reply timeout does).
    } catch (error) {
      console.error("[ChatEngine] Error sending message:", error);
      // Don't clear loading here — a CORS-failed request usually still
      // processed. Realtime clears it on reply; the timeout clears it if not.
    }
  }

  // Send an already-uploaded media file (audio / image / video / document).
  // Shares the exact same webhook contract as text sends — previously this was
  // copy-pasted in ChatSession for audio and files and had drifted (no widgetId).
  async sendMedia(mediaUrl: string, mediaType: string, caption = ""): Promise<void> {
    const messageId = ChatEngine.mintMessageId();
    const mediaMessage: Message = {
      text: caption,
      isUser: true,
      senderType: "user-web",
      createdAt: new Date().toISOString(),
      mediaUrl,
      mediaType,
      clientMessageId: messageId,
    };
    this.config.onMessagesChange((prev: Message[]) => [...prev, mediaMessage]);

    // CORS error expected, webhook still processes
    await this.postWebhookEvent({ type: mediaType, text: caption, mediaUrl }, messageId).catch(() => {});
  }

  // Resolve a contact SERVER-SIDE (api/resolve-contact, service role).
  //
  // The browser must never touch `contacts`: since 4 Aug 2026 the anon role has
  // no privileges on that table (RLS on, all policies {authenticated}), so the
  // old direct insert returned 42501 and every new visitor was blocked. See the
  // header comment in api/resolve-contact.ts.
  private async resolveContact(body: Record<string, unknown>): Promise<string> {
    const res = await fetch(`${WIDGET_API_BASE}/api/resolve-contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organizationId: this.config.organizationId, ...body }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`resolve-contact ${res.status}: ${detail.slice(0, 200)}`);
    }
    const { contactId } = await res.json();
    if (!contactId) throw new Error('resolve-contact returned no contactId');
    return contactId;
  }

  // Create an anonymous contact with "Web Chat User" and a random whatsapp_id
  async createAnonymousContact(): Promise<string> {
    try {
      this.config.onLoadingChange(true);

      const contactId = await this.resolveContact({ mode: 'anonymous' });
      console.log("[ChatEngine] Created anonymous contact:", contactId);

      this.config.contactId = contactId;
      this.config.onContactIdChange(contactId);
      // Brand-new contact — poll from zero (see the adoption path in
      // sendMessage for why).
      this.lastSeenAt = "1970-01-01T00:00:00.000Z";
      this.seenIds.clear();
      this.ensurePolling();

      return contactId;
    } catch (error) {
      console.error("[ChatEngine] Error creating anonymous contact:", error);
      throw error;
    } finally {
      this.config.onLoadingChange(false);
    }
  }

  // Handle user info submission - creates contact and loads existing chat history
  async submitUserInfo(userInfo: UserInfoFormValues): Promise<SubmitUserInfoResult> {
    try {
      this.config.onLoadingChange(true);
      console.log("[ChatEngine] Submitting user info to create/find contact");

      // Find-or-create runs SERVER-SIDE (service role) — the browser has no
      // privileges on `contacts`. Keyed on whatsapp_id, which is the phone for
      // web contacts, exactly as the old client-side lookup was.
      const contactId = await this.resolveContact({
        mode: 'identified',
        phone: userInfo.phone,
        fullName: userInfo.fullName,
        email: userInfo.email || null,
      });
      console.log("[ChatEngine] Resolved contact:", contactId);

      // Handle 2FA if enabled. The PIN is generated, stored, and sent entirely
      // server-side (api/two-fa) — it never touches the browser, which could
      // otherwise read it straight out of the contacts table with the anon key.
      if (this.config.twoFa) {
        const delivery = await this.sendTwoFaCode(contactId, undefined, {
          phone: userInfo.phone,
          fullName: userInfo.fullName,
        });
        if (!delivery.ok) {
          throw new Error('Failed to send verification code');
        }

        // Save contactId but DON'T load history or subscribe yet - waiting for PIN verify
        this.config.onContactIdChange(contactId);
        return { twoFaPending: true, contactId, delivery };
      }

      // Save contactId
      this.config.contactId = contactId;
      this.config.onContactIdChange(contactId);

      // Load existing chat history and start polling for this contact
      await this.loadMessages(contactId);
      this.ensurePolling();

      return { twoFaPending: false, contactId };

    } catch (error) {
      console.error("[ChatEngine] Error submitting user info:", error);
      throw error;
    } finally {
      this.config.onLoadingChange(false);
    }
  }

  // Send (or re-send) the 2FA code. The channel is a REQUEST, not a guarantee:
  // api/two-fa sends on the org's own WhatsApp template or the org-branded
  // email and reports back which one it actually used.
  async sendTwoFaCode(
    contactId: string,
    channel?: TwoFaChannel,
    hints?: { phone?: string; fullName?: string }
  ): Promise<TwoFaDelivery> {
    try {
      const res = await fetch(`${WIDGET_API_BASE}/api/two-fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send',
          contactId,
          channel,
          phone: hints?.phone,
          fullName: hints?.fullName,
        }),
      });
      if (!res.ok) return { ok: false };

      const data = await res.json().catch(() => ({}));
      return { ok: true, channel: data?.channel, sentTo: data?.sent_to };
    } catch (error) {
      console.error('[ChatEngine] Error sending 2FA code:', error);
      return { ok: false };
    }
  }

  // Verify a 2FA PIN for a contact — server-side compare + clear (api/two-fa).
  async verifyTwoFaPin(contactId: string, enteredPin: string): Promise<boolean> {
    try {
      const res = await fetch(`${WIDGET_API_BASE}/api/two-fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', contactId, pin: enteredPin }),
      });
      if (!res.ok) return false;

      const data = await res.json();
      if (!data.ok) return false;

      // Now load history and start polling
      this.config.contactId = contactId;
      await this.loadMessages(contactId);
      this.ensurePolling();
      return true;
    } catch (error) {
      console.error("[ChatEngine] Error verifying 2FA PIN:", error);
      return false;
    }
  }

  // Update configuration
  updateConfig(newConfig: Partial<ChatEngineConfig>): void {
    const oldContactId = this.config.contactId;

    this.config = { ...this.config, ...newConfig };

    // Only restart polling if contactId actually changed
    const contactIdChanged = newConfig.contactId !== undefined && newConfig.contactId !== oldContactId;

    if (contactIdChanged) {
      this.lastSeenAt = null;
      this.seenIds.clear();
      this.ensurePolling();
    }
  }

  // Cleanup resources
  cleanup(): void {
    console.log("[ChatEngine] Cleaning up resources");

    this.clearReplyTimeout();
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    document.removeEventListener("visibilitychange", this.onVisibility);
  }
}
